/**
 * Cliente del CRUD de project_members.
 * Backend: app/api/v1/project_members.py.
 *
 * Sin UI todavía — la consume el wizard de creación de proyecto y el
 * tab "Equipo & RACI" del Project Overview en B.5.e.
 */

import type { TeamSection } from '../lib/tiers'
import { apiFetch } from './client'
import type { UserTenantRole } from './me'

export type RaciRole = 'R' | 'A' | 'C' | 'I'

export interface ProjectMember {
  id: string
  project_id: string
  user_id: string | null
  external_email: string | null
  /** Roles funcionales — etiquetas organizacionales, NO permisos
   * (OT-0008). Uno o más por persona (OT-0058): quien prepara la preventa
   * también puede aprobarla como gerente comercial. Son `template_key`
   * del catálogo de roles. */
  roles: string[]
  /** OT-0058 §4.5 — en qué parte del equipo participa en ESTE proyecto.
   *  Equipo & RACI parte sus secciones por este campo, no por el tier. */
  section: TeamSection
  raci_role: RaciRole | null
  /** Doc 21 Notif-1.d. Si true, el member recibe TODAS las notificaciones
   * del proyecto (commet.*, stage.*, artifact.*), no solo aquellas en las
   * que su RACI lo asigna. Útil para PM/PO o stakeholders interesados. */
  subscribe_all_events: boolean
  created_at: string
  updated_at: string
  /** Resuelto vía JOIN en GET /projects/{id}/members. Sólo presente cuando
   * el member tiene `user_id` (internals + clients onboarded). Las
   * respuestas de POST/PATCH lo dejan en null — re-fetch de la lista lo
   * popula. */
  user_display_name: string | null
  user_email: string | null
  /** OT-0008 §4.3 — tier de licencia. Las tres secciones de Equipo &
   *  RACI se parten por ESTE campo, no por el rol funcional: el rol es
   *  etiqueta organizacional y no dice qué puede hacer la persona.
   *
   *  D6 — reemplaza al viejo `scope`. Un miembro sin cuenta todavía
   *  (invitado por correo) llega como `external`: solo a un externo se
   *  le invita así. `null` solo si el backend no pudo resolverlo. */
  user_tier: UserTenantRole | null
}

export async function listProjectMembers(
  accessToken: string,
  projectId: string,
): Promise<ProjectMember[]> {
  return apiFetch<ProjectMember[]>(
    `/api/v1/projects/${projectId}/members`,
    { accessToken },
  )
}

export interface AddMemberBody {
  /** `user_id` para alguien que ya está en el tenant; `external_email`
   *  para invitar a un externo. Al menos uno. */
  roles: string[]
  user_id?: string
  external_email?: string
  /** Sin valor, el backend usa la de su licencia (admin/editor →
   *  operativo, revisor → extendido, externo → stakeholder). */
  section?: TeamSection
  raci_role?: RaciRole
  /** Doc 21 Notif-1.d. Si true, el member recibe TODAS las notificaciones
   * del proyecto. Default backend = false; el admin puede pre-marcarlo. */
  subscribe_all_events?: boolean
}

/** Response del POST con flags del smart-routing del backend (Doc 12).
 *
 * Para `INTERNAL` y `CLIENT_STAKEHOLDER` con `user_id` directo, todos
 * los flags son `false` y el caller solo lee `member`.
 *
 * Para `CLIENT_STAKEHOLDER` con `external_email` los flags indican
 * qué pasó:
 * - `linked_existing_user`: el email ya tenía cuenta activa en el
 *   tenant → se ligó directo (caso 1).
 * - `invite_already_pending`: ya había invite pendiente (caso 3) → no
 *   se duplica.
 * - `invite_created`: el service creó un invite nuevo (caso 4 — el
 *   típico "invitar al cliente").
 *
 * Frontend usa los flags para mostrar el toast adecuado al admin.
 */
export interface AddMemberResponse {
  member: ProjectMember
  linked_existing_user: boolean
  invite_already_pending: boolean
  invite_created: boolean
}

export async function addProjectMember(
  accessToken: string,
  projectId: string,
  body: AddMemberBody,
): Promise<AddMemberResponse> {
  return apiFetch<AddMemberResponse>(
    `/api/v1/projects/${projectId}/members`,
    { accessToken, method: 'POST', body },
  )
}

export interface UpdateMemberBody {
  /** Reemplaza la lista completa de roles del miembro (al menos uno). */
  roles?: string[]
  /** OT-0058 §4.5 — mover de sección. Salir del operativo se rechaza si es
   *  el líder o responsable de documentos. */
  section?: TeamSection
  raci_role?: RaciRole
  /** Doc 21 Notif-1.d. None = no cambiar; true/false = setear explícito. */
  subscribe_all_events?: boolean
}

export async function updateProjectMember(
  accessToken: string,
  projectId: string,
  memberId: string,
  body: UpdateMemberBody,
): Promise<ProjectMember> {
  return apiFetch<ProjectMember>(
    `/api/v1/projects/${projectId}/members/${memberId}`,
    { accessToken, method: 'PATCH', body },
  )
}

/**
 * Quita un miembro del proyecto.
 *
 * OT-0008 §4.4 — si el miembro es dueño de artefactos con borrador
 * abierto, el backend responde 409 `MEMBER_HAS_OPEN_DRAFTS` con
 * `details.count` y NO quita a nadie. El caller debe reintentar
 * pasando `reassignOwnedTo` (user_id del nuevo dueño). Los artefactos
 * suyos sin borrador abierto se liberan solos (owner → null).
 */
export async function removeProjectMember(
  accessToken: string,
  projectId: string,
  memberId: string,
  reassignOwnedTo?: string,
): Promise<void> {
  const query = reassignOwnedTo
    ? `?reassign_owned_to=${encodeURIComponent(reassignOwnedTo)}`
    : ''
  return apiFetch<void>(
    `/api/v1/projects/${projectId}/members/${memberId}${query}`,
    { accessToken, method: 'DELETE' },
  )
}
